import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { getEmployeeBadges } from "@/api";

const DEFAULT_ATTEMPTS = 8;
const DEFAULT_DELAY_MS = 450;

const LEVEL_NUM_MAP = {
  1: "Apprentice",
  2: "Professional",
  3: "Coach",
  4: "Master",
};

/**
 * A checkpoint counts toward “award badge” eligibility only when explicitly passed.
 * Failed, pending, not applicable, and other states do not qualify.
 */
export function isCheckpointCompleteForAward(cp) {
  if (!cp || typeof cp !== "object") return false;
  const raw = String(cp.evaluation_status ?? cp.status ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
  return raw === "passed";
}

const BADGE_LEVEL_ORDER = {
  apprentice: 1,
  proficient: 2,
  coach: 3,
  architect: 4,
};

/**
 * A proficiency tier is eligible for “award badge” when every checkpoint at that tier
 * and every tier below it is passed (badge rules Rule 6 / Rule 9).
 */
export function isLevelCompleteForAward(level, checkpoints) {
  if (!level || !checkpoints?.length) return false;
  const targetOrder = BADGE_LEVEL_ORDER[normalizeBadgeLevel(String(level))];
  if (!targetOrder) return false;
  const relevantCheckpoints = checkpoints.filter(
    (cp) => (BADGE_LEVEL_ORDER[normalizeBadgeLevel(cp.level || "")] ?? 0) <= targetOrder
  );
  if (!relevantCheckpoints.length) return false;
  return relevantCheckpoints.every((cp) => isCheckpointCompleteForAward(cp));
}

/** Same as Path to Readiness `extractCode`: first `#digits` in string. */
export function extractHashResponsibilityCode(str) {
  if (str == null || str === "") return "";
  const m = String(str).match(/#(\d+)/);
  return m ? `#${m[1]}` : "";
}

function issuedCodeNorm(badge) {
  const c =
    badge?.issued_for?.code ?? badge?.responsibility?.code ?? badge?.responsibility?.code_str ?? "";
  const hash = extractHashResponsibilityCode(c);
  if (hash) return hash;
  return c ? String(c).replace(/\s/g, "") : "";
}

function targetCodeFromContext(ctx) {
  if (!ctx || ctx.responsibilityCode == null || ctx.responsibilityCode === "") {
    return "";
  }
  const raw = ctx.responsibilityCode;
  return extractHashResponsibilityCode(raw) || String(raw).trim();
}

/**
 * Match a badge row to a responsibility (id and/or code), aligned with Path to Readiness.
 * @param {object} [matchContext] - e.g. `{ responsibilityCode: '#123' }` when ids are inconsistent
 */
export function badgeMatchesResponsibility(badge, responsibilityId, matchContext = {}) {
  if (responsibilityId == null || responsibilityId === "") return false;
  const rid = String(responsibilityId);
  const idCandidates = [
    badge.issued_for_id,
    badge.issued_for?.id,
    badge.responsibility?.id,
    badge.responsibility_id,
  ]
    .filter((x) => x != null && x !== "")
    .map((x) => String(x));
  if (idCandidates.some((id) => id === rid)) return true;

  const targetName =
    matchContext.responsibilityName != null ? String(matchContext.responsibilityName).trim() : "";
  if (targetName !== "") {
    const issuedName = (badge.issued_for?.name || badge.responsibility?.name || "").trim();
    if (issuedName && issuedName.toLowerCase() === targetName.toLowerCase()) {
      return true;
    }
  }

  const targetCode = targetCodeFromContext(matchContext);
  if (!targetCode) return false;
  const ic = issuedCodeNorm(badge);
  if (ic && ic === targetCode) return true;
  return false;
}

function isCurrentFlag(b) {
  return b.is_current === true || b.is_current === 1 || b.is_current === "1";
}

function isExplicitlyNotCurrent(b) {
  const ic = b?.is_current;
  return ic === false || ic === 0 || ic === "0" || ic === "false";
}

function issuedTs(b) {
  const raw = b?.issued_on || b?.created_at || b?.updated_at || b?.currentBadge?.issued_on;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function numId(b) {
  const n = Number(b?.id ?? b?.currentBadge?.id);
  return Number.isFinite(n) ? n : 0;
}

function newerBadgeRow(a, b) {
  const ta = issuedTs(a);
  const tb = issuedTs(b);
  if (tb !== ta) return tb > ta ? b : a;
  const na = numId(a);
  const nb = numId(b);
  if (nb !== na) return nb > na ? b : a;
  return b;
}

/**
 * Pick the current badge row for a responsibility (Path to Readiness parity).
 * Handles missing / delayed `is_current` and optional responsibility code matching.
 */
export function pickBadgeForResponsibility(employeeBadges, responsibilityId, matchContext = {}) {
  if (!Array.isArray(employeeBadges) || !employeeBadges.length) return null;
  if (responsibilityId == null || responsibilityId === "") return null;

  const matched = employeeBadges.filter((b) =>
    badgeMatchesResponsibility(b, responsibilityId, matchContext)
  );
  if (!matched.length) return null;

  const currentFlagged = matched.filter(isCurrentFlag);
  if (currentFlagged.length === 1) return currentFlagged[0];
  if (currentFlagged.length > 1) {
    return currentFlagged.reduce(newerBadgeRow);
  }

  const candidates = matched.filter((b) => !isExplicitlyNotCurrent(b));
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  return candidates.reduce(newerBadgeRow);
}

/**
 * Map API badge row → inspection pane chip (Path to Readiness `badgeToInspectionPanePayload` parity).
 */
export function mapBadgeRowToInspectionChip(badgeRow) {
  if (!badgeRow) return null;
  const rawLevel =
    badgeRow.level ??
    badgeRow.proficiency_level ??
    badgeRow.currentBadge?.level ??
    badgeRow.currentBadge?.proficiency_level;
  let levelStr = "Novice";
  if (rawLevel != null && rawLevel !== "") {
    if (typeof rawLevel === "string") {
      levelStr = rawLevel;
    } else if (typeof rawLevel === "number") {
      levelStr = LEVEL_NUM_MAP[rawLevel] || String(rawLevel);
    } else {
      levelStr = String(rawLevel);
    }
  }
  const statusRaw = badgeRow.status ?? badgeRow.currentBadge?.status ?? "";
  const dot = statusRaw === "good_standing" ? "Good Standing" : "Given";
  return {
    level: levelStr,
    dot,
    id: badgeRow.id ?? badgeRow.currentBadge?.id ?? null,
    expiration_date: badgeRow.expiration_date ?? badgeRow.currentBadge?.expiration_date ?? null,
    expires_in_days_from_becoming_obsolete:
      badgeRow.expires_in_days_from_becoming_obsolete ??
      badgeRow.currentBadge?.expires_in_days_from_becoming_obsolete ??
      null,
    has_expiration_if_obsolete:
      badgeRow.has_expiration_if_obsolete ??
      badgeRow.expires_when_obsolete ??
      badgeRow.currentBadge?.has_expiration_if_obsolete ??
      false,
  };
}

/**
 * After create/award, GET badges can lag. Retry with Path-aligned pick + optional code context.
 */
export async function fetchCurrentResponsibilityBadgeWithRetry(
  accountId,
  employeeId,
  responsibilityId,
  options = {}
) {
  const attempts = options.attempts ?? DEFAULT_ATTEMPTS;
  const delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
  const matchContext = options.matchContext ?? {};
  for (let i = 0; i < attempts; i++) {
    const badgesRes = await getEmployeeBadges(accountId, employeeId);
    const employeeBadges = badgesRes?.data?.results || [];
    const current = pickBadgeForResponsibility(employeeBadges, responsibilityId, matchContext);
    if (current) return current;
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
}
